import { MergeCompaniesContext } from "@/services/MergeCompaniesContext/mergeCompaniesContext";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shadcn/components/ui/card";
import { DBBranchesByCompanyID } from "@/types";
import React, { useContext } from "react";

type MergeCompaniesDisplayContainerProps = {
  side: "keep" | "remove";
};

const MergeCompaniesDisplayContainer: React.FC<
  MergeCompaniesDisplayContainerProps
> = ({ side }) => {
  const { mergedCompany, removeSideCompany } = useContext(
    MergeCompaniesContext
  );
  const displayCompany: DBBranchesByCompanyID | null =
    side === "keep" ? mergedCompany : removeSideCompany;

  if (!displayCompany) return null;

  // Shows all company and branch details in read-only form, so users can review before merging
  return (
    <Card className="m-auto w-full max-w-2xl p-4">
      <CardHeader>
        <CardTitle className="text-center text-xl font-semibold">
          {displayCompany.company_name}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <div className="grid grid-cols-1 gap-x-8 gap-y-4 md:grid-cols-2">
            <div className="min-w-[180px] flex-1">
              <div className="font-medium">Company Type</div>
              <div>
                {displayCompany.company_type === "private"
                  ? "Private"
                  : "Business"}
              </div>
            </div>
            <div className="min-w-[180px] flex-1">
              <div className="font-medium">Registration Number</div>
              <div>
                {displayCompany.registration_number || (
                  <span className="text-gray-400">N/A</span>
                )}
              </div>
            </div>
            <div className="min-w-[180px] flex-1">
              <div className="font-medium">VAT Number</div>
              <div>
                {displayCompany.tax_number || (
                  <span className="text-gray-400">N/A</span>
                )}
              </div>
            </div>
            <div className="min-w-[180px] flex-1">
              <div className="font-medium">Contact Number</div>
              <div>
                {displayCompany.phone_number || (
                  <span className="text-gray-400">N/A</span>
                )}
              </div>
            </div>
            <div className="min-w-[180px] flex-1">
              <div className="font-medium">Industry Type</div>
              <div>
                {displayCompany.industry_type || (
                  <span className="text-gray-400">N/A</span>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="mt-6">
          <div className="mb-2 text-lg font-semibold">Branches</div>
          <div className="space-y-4">
            {displayCompany.branches.length === 0 && (
              <div className="text-gray-400">No branches found</div>
            )}
            {displayCompany.branches.map((branch) => (
              <div key={branch.branch_id} className="rounded border p-3">
                <div className="mb-1 font-medium">{branch.branch_name}</div>
                <div className="grid grid-cols-1 gap-x-8 gap-y-1 text-sm md:grid-cols-2">
                  <div>
                    <span className="font-medium">Address 1:</span>{" "}
                    {branch.branch_address1 || (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </div>

                  <div>
                    <span className="font-medium">Address 2:</span>{" "}
                    {branch.branch_address2 || (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </div>

                  <div>
                    <span className="font-medium">Suburb:</span>{" "}
                    {branch.branch_suburb || (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </div>

                  <div>
                    <span className="font-medium">City:</span>{" "}
                    {branch.branch_city || (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </div>
                  <div>
                    <span className="font-medium">Province:</span>{" "}
                    {branch.branch_province || (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </div>
                  <div>
                    <span className="font-medium">Country:</span>{" "}
                    {branch.branch_country || (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </div>

                  <div>
                    <span className="font-medium">Office Park:</span>{" "}
                    {branch.branch_office_park_name || (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </div>

                  <div>
                    <span className="font-medium">Postal Code:</span>{" "}
                    {branch.branch_pin_code || (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </div>
                  <div>
                    <span className="font-medium">Latitude:</span>{" "}
                    {branch.branch_latitude || (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </div>
                  <div>
                    <span className="font-medium">Longitude:</span>{" "}
                    {branch.branch_longitude || (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default MergeCompaniesDisplayContainer;
